import { useEffect, useRef, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie,
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { getDeviationsByPeriod, Area, DeviationType, HercaRecord } from '../api';
import { api } from '../api/client';
import Spinner from '../components/common/Spinner';
import './Page.css';
import './Reports.css';

type Preset = 'quarter' | 'semi' | 'annual' | 'custom';

const COLORS = ['#7B2D8B', '#9B4DCA', '#C084FC', '#5A1D6A', '#D8B4FE', '#A855F7', '#6D28D9', '#4C1D95'];

function getPeriodDates(preset: Preset, custom: { start: string; end: string }) {
  const now = new Date();
  if (preset === 'custom') return { start: custom.start, end: custom.end };
  const end = now.toISOString().split('T')[0];
  const startDate = new Date(now);
  if (preset === 'quarter') startDate.setMonth(now.getMonth() - 3);
  if (preset === 'semi') startDate.setMonth(now.getMonth() - 6);
  if (preset === 'annual') startDate.setFullYear(now.getFullYear() - 1);
  return { start: startDate.toISOString().split('T')[0], end };
}

const PRESET_LABELS: Record<Preset, string> = {
  quarter: 'Last Quarter',
  semi: 'Last 6 Months',
  annual: 'Last Year',
  custom: 'Custom Range',
};

async function downloadSummaryPdf(
  title: string,
  subtitle: string,
  stats: { label: string; value: number }[],
  areaData: { name: string; count: number }[],
  typeData: { name: string; value: number }[],
  employeeData: { name: string; count: number }[]
) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;
  const contentWidth = pageWidth - margin * 2;

  // Header bar
  doc.setFillColor(123, 45, 139);
  doc.rect(0, 0, pageWidth, 80, 'F');

  doc.setFontSize(20);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, 38);

  doc.setFontSize(10);
  doc.setTextColor(216, 180, 254);
  doc.setFont('helvetica', 'normal');
  doc.text(subtitle, margin, 60);

  // Stat boxes
  const gap = 12;
  const boxW = (contentWidth - gap * 3) / 4;
  const boxH = 72;
  const boxY = 100;

  stats.forEach((stat, i) => {
    const x = margin + i * (boxW + gap);
    doc.setFillColor(245, 238, 255);
    doc.roundedRect(x, boxY, boxW, boxH, 6, 6, 'F');
    doc.setDrawColor(200, 170, 230);
    doc.setLineWidth(0.5);
    doc.roundedRect(x, boxY, boxW, boxH, 6, 6, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.setTextColor(123, 45, 139);
    const valStr = String(stat.value);
    doc.text(valStr, x + boxW / 2, boxY + 38, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(122, 92, 138);
    doc.text(stat.label, x + boxW / 2, boxY + 56, { align: 'center' });
  });

  // Two tables side by side: Area | Type
  const tableY = boxY + boxH + 24;
  const halfW = (contentWidth - 20) / 2;
  const rightMarginForLeft = pageWidth - (margin + halfW);
  const leftMarginForRight = margin + halfW + 20;

  autoTable(doc, {
    startY: tableY,
    margin: { left: margin, right: rightMarginForLeft },
    head: [['Area', 'Deviations']],
    body: areaData.map((r) => [r.name, r.count]),
    styles: { fontSize: 9, cellPadding: 5 },
    headStyles: { fillColor: [123, 45, 139], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 238, 255] },
  });
  const areaFinalY = (doc as any).lastAutoTable.finalY;

  autoTable(doc, {
    startY: tableY,
    margin: { left: leftMarginForRight, right: margin },
    head: [['Deviation Type', 'Count']],
    body: typeData.map((r) => [r.name, r.value]),
    styles: { fontSize: 9, cellPadding: 5 },
    headStyles: { fillColor: [155, 77, 202], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 238, 255] },
  });
  const typeFinalY = (doc as any).lastAutoTable.finalY;

  // All employees table
  autoTable(doc, {
    startY: Math.max(areaFinalY, typeFinalY) + 20,
    margin: { left: margin, right: margin },
    head: [['Employee', 'Deviations']],
    body: employeeData.map((r) => [r.name, r.count]),
    styles: { fontSize: 9, cellPadding: 5 },
    headStyles: { fillColor: [74, 29, 149], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 238, 255] },
  });

  doc.save('Deviation_Summary_Report.pdf');
}

async function downloadPdf(
  chartRef: { current: HTMLDivElement | null },
  title: string,
  subtitle: string,
  headers: string[],
  rows: (string | number)[][]
) {
  if (!chartRef.current) return;

  const canvas = await html2canvas(chartRef.current, { scale: 2, useCORS: true });
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;

  // Header
  doc.setFontSize(18);
  doc.setTextColor(45, 27, 61);
  doc.text(title, margin, 44);

  doc.setFontSize(10);
  doc.setTextColor(122, 92, 138);
  doc.text(subtitle, margin, 60);

  // Chart image
  const imgWidth = pageWidth - margin * 2;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  doc.addImage(canvas.toDataURL('image/png'), 'PNG', margin, 72, imgWidth, imgHeight);

  // Data table
  autoTable(doc, {
    startY: 72 + imgHeight + 16,
    head: [headers],
    body: rows,
    styles: { fontSize: 10, cellPadding: 6 },
    headStyles: { fillColor: [123, 45, 139], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 238, 255] },
    margin: { left: margin, right: margin },
  });

  doc.save(`${title.replace(/\s+/g, '_')}.pdf`);
}

const Reports = () => {
  const [preset, setPreset] = useState<Preset>('quarter');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedDevType, setSelectedDevType] = useState('');
  const [areas, setAreas] = useState<Area[]>([]);
  const [devTypes, setDevTypes] = useState<DeviationType[]>([]);
  const [data, setData] = useState<{ deviations: HercaRecord[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const areaChartRef = useRef<HTMLDivElement>(null);
  const typeChartRef = useRef<HTMLDivElement>(null);
  const empChartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get<Area[]>('/admin/areas').then(setAreas).catch(() => {});
    api.get<DeviationType[]>('/admin/deviations').then(setDevTypes).catch(() => {});
  }, []);

  const runReport = () => {
    const { start, end } = getPeriodDates(preset, custom);
    if (!start || !end) return;
    setLoading(true);
    setError('');
    getDeviationsByPeriod(start, end, selectedArea || undefined)
      .then((d) => setData({ deviations: d.deviations ?? [] }))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { runReport(); }, [preset, selectedArea]);

  const visible: HercaRecord[] = (data?.deviations ?? []).filter((r) =>
    selectedDevType ? r.deviationGapId?._id === selectedDevType : true
  );

  const byArea: Record<string, number> = {};
  const byEmployee: Record<string, number> = {};
  const byType: Record<string, number> = {};

  visible.forEach((r) => {
    const area = r.areaId?.name ?? 'Unknown';
    const emp = r.employeeId?.name ?? 'Unknown';
    const type = r.deviationGapId?.name ?? 'Unknown';
    byArea[area] = (byArea[area] ?? 0) + 1;
    byEmployee[emp] = (byEmployee[emp] ?? 0) + 1;
    byType[type] = (byType[type] ?? 0) + 1;
  });

  const areaChartData = Object.entries(byArea).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  const allEmployeeData = Object.entries(byEmployee).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  const employeeChartData = allEmployeeData.slice(0, 10);
  const typeChartData = Object.entries(byType).map(([name, value], i) => ({ name, value, fill: COLORS[i % COLORS.length] })).sort((a, b) => b.value - a.value);

  const periodLabel = `${PRESET_LABELS[preset]} · Generated ${new Date().toLocaleDateString('en-GB')}`;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports & Analytics</h1>
          <p className="page-subtitle">Deviation insights by period, area, employee, and type</p>
        </div>
      </div>

      <div className="report-filters">
        <div className="preset-tabs">
          {(Object.keys(PRESET_LABELS) as Preset[]).map((p) => (
            <button
              key={p}
              className={`preset-tab${preset === p ? ' preset-tab--active' : ''}`}
              onClick={() => setPreset(p)}
            >
              {PRESET_LABELS[p]}
            </button>
          ))}
        </div>

        {preset === 'custom' && (
          <div className="custom-range">
            <input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} />
            <span>to</span>
            <input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} />
            <button className="btn-primary" style={{ padding: '9px 18px' }} onClick={runReport}>Apply</button>
          </div>
        )}

        <select className="area-filter" value={selectedArea} onChange={(e) => setSelectedArea(e.target.value)}>
          <option value="">All Areas</option>
          {areas.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
        </select>

        <select className="area-filter" value={selectedDevType} onChange={(e) => setSelectedDevType(e.target.value)}>
          <option value="">All Deviation Types</option>
          {devTypes.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
        </select>
      </div>

      {loading && <Spinner />}
      {error && <p className="error-banner">{error}</p>}

      {data && !loading && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div className="report-stat-row" style={{ flex: 1, marginBottom: 0 }}>
              <div className="report-stat">
                <span className="stat-value">{visible.length}</span>
                <span className="stat-label">Total Deviations</span>
              </div>
              <div className="report-stat">
                <span className="stat-value">{areaChartData.length}</span>
                <span className="stat-label">Areas Affected</span>
              </div>
              <div className="report-stat">
                <span className="stat-value">{allEmployeeData.length}</span>
                <span className="stat-label">Employees Involved</span>
              </div>
              <div className="report-stat">
                <span className="stat-value">{typeChartData.length}</span>
                <span className="stat-label">Deviation Types</span>
              </div>
            </div>
            <button
              className="chart-download-btn"
              style={{ marginLeft: 16, whiteSpace: 'nowrap' }}
              onClick={() => downloadSummaryPdf(
                'Deviation Management Report',
                periodLabel,
                [
                  { label: 'Total Deviations', value: visible.length },
                  { label: 'Areas Affected', value: areaChartData.length },
                  { label: 'Employees Involved', value: allEmployeeData.length },
                  { label: 'Deviation Types', value: typeChartData.length },
                ],
                areaChartData,
                typeChartData,
                allEmployeeData
              )}
            >
              ↓ PDF
            </button>
          </div>

          {visible.length === 0 ? (
            <div className="report-chart-card" style={{ textAlign: 'center', padding: 40, marginTop: 20 }}>
              <p style={{ color: '#7A5C8A', fontSize: 15 }}>No deviations found for the selected filters.</p>
            </div>
          ) : (
            <>
              <div className="report-charts-grid">
                <div className="report-chart-card">
                  <div className="chart-card-header">
                    <h3 className="dash-card-title">Deviations by Area</h3>
                    <button
                      className="chart-download-btn"
                      onClick={() => downloadPdf(
                        areaChartRef,
                        'Deviations by Area',
                        periodLabel,
                        ['Area', 'Deviations'],
                        areaChartData.map((r) => [r.name, r.count])
                      )}
                    >
                      ↓ PDF
                    </button>
                  </div>
                  <div ref={areaChartRef}>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={areaChartData} margin={{ top: 4, right: 16, left: -10, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0E8F7" />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#7A5C8A' }} angle={-30} textAnchor="end" />
                        <YAxis tick={{ fontSize: 11, fill: '#7A5C8A' }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" name="Deviations" fill="#7B2D8B" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="report-chart-card">
                  <div className="chart-card-header">
                    <h3 className="dash-card-title">Deviations by Type</h3>
                    <button
                      className="chart-download-btn"
                      onClick={() => downloadPdf(
                        typeChartRef,
                        'Deviations by Type',
                        periodLabel,
                        ['Deviation Type', 'Count'],
                        typeChartData.map((r) => [r.name, r.value])
                      )}
                    >
                      ↓ PDF
                    </button>
                  </div>
                  <div ref={typeChartRef}>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={typeChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={90}
                          label={({ name, percent }: { name?: string; percent?: number }) =>
                            `${name ?? ''} ${((percent ?? 0) * 100).toFixed(0)}%`
                          }
                        />
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="report-chart-card report-chart-card--full">
                <div className="chart-card-header">
                  <h3 className="dash-card-title">Top 10 Employees by Deviation Count</h3>
                  <button
                    className="chart-download-btn"
                    onClick={() => downloadPdf(
                      empChartRef,
                      'Top Employees by Deviation Count',
                      periodLabel,
                      ['Employee', 'Deviations'],
                      allEmployeeData.map((r) => [r.name, r.count])
                    )}
                  >
                    ↓ PDF
                  </button>
                </div>
                <div ref={empChartRef}>
                  <ResponsiveContainer width="100%" height={Math.max(280, employeeChartData.length * 36)}>
                    <BarChart data={employeeChartData} layout="vertical" margin={{ top: 4, right: 40, left: 100, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0E8F7" />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#7A5C8A' }} allowDecimals={false} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: '#2D1B3D' }} width={95} />
                      <Tooltip />
                      <Bar dataKey="count" name="Deviations" fill="#9B4DCA" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
